import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, 
  FileText, 
  Users, 
  Award, 
  ArrowLeft, 
  Clock, 
  Heart, 
  Download,
  Award as AwardIcon,
  Calendar,
  Star,
  Baby,
  Activity,
  GraduationCap,
  Scissors,
  Eye,
  Syringe,
  Shield
} from 'lucide-react';
import { articlesAPI, consultationsAPI, statsAPI } from '../utils/api';
import '../styles/HomePage.css';

const HomePage = () => {
  const [stats, setStats] = useState({
    consultations: 0,
    articles: 0,
    doctors: 0,
    visitors: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // جلب البيانات من الـ API
      const [articlesData, consultationsData, statsData] = await Promise.all([
        articlesAPI.getAll(),
        consultationsAPI.getAll(),
        statsAPI.getStats()
      ]);

      setStats({
        consultations: consultationsData.length || 0,
        articles: articlesData.length || 0,
        doctors: statsData?.doctors || 50,
        visitors: statsData?.visitors || 10000
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
      // استخدام قيم افتراضية في حالة الخطأ
      setStats({
        consultations: 0,
        articles: 0,
        doctors: 50,
        visitors: 10000
      });
    } finally {
      setLoading(false);
    }
  };

  const doctorSpecialties = [
    { icon: <Baby />, title: 'استشارية النساء والتوليد', description: 'خبرة 35 عاماً في دبي' },
    { icon: <Activity />, title: 'تشخيص الحمول عالية الخطورة', description: 'الداء السكري، ارتفاع التوتر، الانسمام الحملي' },
    { icon: <Star />, title: 'الولادة الطبيعية', description: 'تقديم النصائح لولادة طبيعية ناجحة' },
    { icon: <AwardIcon />, title: 'الولادة بعد القيصرية', description: 'إمكانية نجاح ولادة طبيعية بعد قيصرية سابقة' },
    { icon: <Calendar />, title: 'التشخيص المبكر', description: 'تشخيص آفات الثلث الأول من الحمل' },
    { icon: <Scissors />, title: 'الإجراءات التجميلية', description: 'تضييق المهبل وعلاج جفافه بالليزر' },
    { icon: <Eye />, title: 'جراحة المناظير', description: 'عمليات التنظير البطني المتقدمة' },
    { icon: <Syringe />, title: 'العلاج الدوائي', description: 'علاج داء البطانة الهاجرة والأورام' },
    { icon: <GraduationCap />, title: 'أمراض سن اليأس', description: 'تشخيص وعلاج أمراض ما بعد انقطاع الطمث' },
    { icon: <Shield />, title: 'فحوصات المتابعة', description: 'متابعة الحمول الرحوية والحمل العنقودي' }
  ];

  const handleDownloadCV = () => {
    // إنشاء ملف PDF وهمي للتحميل
    const fileName = '../../public/besema CV.docx';
    const fileContent =  `
      سيرة ذاتية - الدكتورة باسمة جابر
      استشارية النساء والتوليد
      
      الخبرة العملية:
      - 35 سنة خبرة في طب النساء والتوليد
      - استشارية النساء والتوليد في دبي
      - متخصصة في تشخيص الحمول عالية الخطورة
      - خبيرة في الولادة الطبيعية والقيصرية
      
      المؤهلات:
      - شهادة البورد في طب النساء والتوليد
      - دبلوم جراحة التجميل النسائية
      - دبلوم الإخصاب المساعد
      
      التخصصات:
      - تشخيص آفات الحمل المبكر
      - جراحة المناظير الدقيقة
      - علاج أمراض سن اليأس
      - الإجراءات التجميلية النسائية
      
      للتواصل:
      البريد الإلكتروني: info@drbasema.com
      الهاتف: +971 50 123 4567
    `;
    
    // إنشاء blob من المحتوى
    const blob = new Blob([fileContent], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const features = [
    {
      icon: Stethoscope,
      title: 'استشارات طبية',
      description: 'احصل على استشارات طبية موثوقة من أطباء متخصصين',
      color: 'from-blue-500 to-blue-600',
      link: '/consultations'
    },
    {
      icon: FileText,
      title: 'مقالات طبية',
      description: 'اقرأ مقالات متخصصة ومعلومات صحية مفيدة',
      color: 'from-purple-500 to-purple-600',
      link: '/articles'
    },
    {
      icon: Users,
      title: 'فريق متخصص',
      description: 'فريق من الأطباء والمتخصصين في مختلف المجالات',
      color: 'from-green-500 to-green-600',
      link: '/about'
    },
    {
      icon: Award,
      title: 'جودة عالية',
      description: 'محتوى طبي موثوق ومراجع من قبل خبراء',
      color: 'from-orange-500 to-orange-600',
      link: '/about'
    }
  ];

  const statsData = [
    { number: `${stats.consultations}+`, label: 'استشارة طبية', icon: Stethoscope },
    { number: `${stats.articles}+`, label: 'مقال متخصص', icon: FileText },
    { number: `${stats.doctors}+`, label: 'طبيب متخصص', icon: Users },
    { number: `${stats.visitors}+`, label: 'زائر شهرياً', icon: Heart }
  ];

  if (loading) {
    return (
      <div className="homepage-loading-container">
        <div className="homepage-loading-spinner"></div>
        <p className="homepage-loading-text">جاري تحميل البيانات...</p>
      </div>
    );
  }

  return (
    <div className="homepage" dir="rtl">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              مرحباً بك في العيادة الطبية
            </h1>
            <p className="hero-subtitle">
              منصتك الموثوقة للحصول على استشارات طبية ومقالات صحية متخصصة
            </p>
            <div className="hero-buttons">
              <Link
                to="/consultations"
                className="primary-button"
              >
                تصفح الاستشارات
                <ArrowLeft className="button-icon" />
              </Link>
              <Link
                to="/articles"
                className="secondary-button"
              >
                اقرأ المقالات
                <ArrowLeft className="button-icon" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Full Width Doctor Image Section */}
      <section className="doctor-full-image-section">
        <div className="full-image-container">
          <img 
            src="../../images/IMG_0463-e1558711550776-1500x630.webp" 
            alt="الدكتورة باسمة جبر - استشارية النساء والتوليد"
            className="doctor-full-image"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/1920x600/3b82f6/ffffff?text=Dr.+Basema+Jaber+-+استشارية+النساء+والتوليد';
            }}
          />
          <div className="full-image-overlay">
            <div className="overlay-content">
              <h2 className="overlay-title">الدكتورة باسمة جبر</h2>
              <p className="overlay-subtitle">استشارية النساء والتوليد في دبي</p>
              <div className="overlay-experience">
                <Calendar className="overlay-icon" />
                <span>35+ سنة خبرة</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Profile Section */}
      <section className="doctor-profile-section" >
        <div className="doctor-profile-container">
          <div className="doctor-info-wrapper">
            <div className="doctor-header">
              <h2 className="doctor-name">الدكتورة باسمة جبر</h2>
              <p className="doctor-specialty">استشارية النساء والتوليد في دبي</p>
              <div className="doctor-education">
                <span className="education-badge">خبرة 35 عاماً</span>
                <span className="education-badge">متخصصة في النساء والتوليد</span>
                <span className="education-badge">دبلوم جراحة التجميل النسائية</span>
                <span className="education-badge">دبلوم الإخصاب المساعد</span>
              </div>
            </div>

            <div className="doctor-description">
              <p className="description-paragraph">
                كاستشارية النساء والتوليد في دبي، لقد كنت محظوظة بحصولي على الخبرة الواسعة التي امتدت 35 عامًا. 
                لقد عملت في مجموعة كبيرة من المستشفيات، بدءاً من المستشفيات الجامعية إلى المستشفيات العامة 
                بالمدن بين دمشق وعمان.
              </p>
              
              <p className="description-paragraph">
                اختصاصاتي في مجال العناية بالحامل وتشخيص وتدبير الحمول عالية الخطورة المترافقة بالداء السكري، 
                وارتفاع التوتر الشرياني والانسمام الحملي مع التركيز على إنجاز الولادة الطبيعية بتقديم جميع 
                النصائح للمرأة التي تكفل وصولها إلى ولادة طبيعية ناجحة، وحتى إمكانية نجاح ولادة طبيعية بعد 
                قيصرية سابقة.
              </p>

              <div className="cv-download-section">
                <button onClick={handleDownloadCV} className="cv-download-button">
                  <Download className="download-icon" />
                  تحميل السيرة الذاتية (PDF)
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Doctor Specialties Grid */}
        <div className="specialties-grid">
          {doctorSpecialties.map((specialty, index) => (
            <div key={index} className="specialty-card">
              <div className="specialty-icon-wrapper">
                {specialty.icon}
              </div>
              <h3 className="specialty-title">{specialty.title}</h3>
              <p className="specialty-description">{specialty.description}</p>
            </div>
          ))}
        </div>

        {/* Doctor Details */}
        <div className="doctor-details">
          <div className="detail-section">
            <h3 className="detail-title">التشخيص والتدبير</h3>
            <ul className="detail-list">
              <li>تشخيص آفات الثلث الأول من الحمل (الإسقاطات، الحمول خارج الرحم)</li>
              <li>التشخيص المبكر والتدبير بالعلاج الدوائي أو الجراحي عن طريق التنظير البطني</li>
              <li>تشخيص الحمول الرحوية أو الحمل العنقودي وتدبيرها ومتابعتها</li>
              <li>تشخيص آفات عنق الرحم بمنظار الرحم</li>
              <li>تشخيص وتدبير كيسات المبيض والمبيض متعدد الكيسات</li>
            </ul>
          </div>

          <div className="detail-section">
            <h3 className="detail-title">العلاجات والإجراءات</h3>
            <ul className="detail-list">
              <li>علاج الليف الرحمي وداء البطانة الهاجرة جراحياً ودوائياً</li>
              <li>إجراء عمليات تضييق المهبل وعلاج جفاف المهبل باستخدام الليزر</li>
              <li>كافة الإجراءات التجميلية النسائية التي تكفل إعادة الحيوية لأنسجة المهبل</li>
              <li>علاج أمراض سن اليأس</li>
              <li>فحوصات الموجات فوق الصوتية المتخصصة</li>
              <li>متابعة الحمل عالي الخطورة</li>
            </ul>
          </div>
        </div>
      </section>

  


      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">هل لديك استفسار طبي؟</h2>
          <p className="cta-subtitle">تصفح استشاراتنا الطبية أو اقرأ مقالاتنا المتخصصة</p>
          <Link
            to="/consultations"
            className="cta-button"
          >
            ابدأ الآن
            <ArrowLeft className="button-icon" />
          </Link>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="trust-container">
          <Clock className="trust-icon" />
          <h2 className="trust-title">متاح على مدار الساعة</h2>
          <p className="trust-description">
            نحن هنا لخدمتك في أي وقت. تصفح محتوانا الطبي الموثوق واحصل على المعلومات التي تحتاجها
            للحفاظ على صحتك وصحة عائلتك
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;